/**
 * placeName.js — browser-side reverse geocoding for device GPS fixes.
 *
 * Same Nominatim lookup the machine-detail map chip uses ("Gopalapalem"),
 * shared + cached so lists can show a nearby city/village name per row:
 *  - coordinates rounded to 3 decimals (~110 m) — one lookup per spot, ever
 *  - cache in memory + localStorage (survives reloads)
 *  - lookups queued at 1 request/second (Nominatim usage policy)
 *  - usePlaceName(lat, lng) hook re-renders the row when the name arrives
 */
import { useEffect, useState } from 'react';

const LS_KEY = 'placeNameCache.v1';

const mem = new Map();        // "lat3,lng3" -> name
const pending = new Set();    // keys queued or in flight
const queue = [];             // [{key, lat, lng}]
const subscribers = new Map(); // key -> Set<fn>
let running = false;
let lsLoaded = false;
let saveTimer = null;

const keyOf = (lat, lng) => `${lat.toFixed(3)},${lng.toFixed(3)}`;

function loadCache() {
    if (lsLoaded) return;
    lsLoaded = true;
    try {
        const raw = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
        Object.entries(raw).forEach(([k, v]) => mem.set(k, v));
    } catch (e) { /* corrupt cache — start fresh */ }
}

function persistCache() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(Object.fromEntries(mem)));
        } catch (e) { /* storage full — memory cache still works */ }
    }, 1000);
}

// Nearby city / village name — same field order as the detail-page map chip.
function shortName(addr = {}, displayName = '') {
    return addr.city || addr.town || addr.village || addr.suburb ||
        addr.state_district || addr.county || addr.state ||
        (displayName ? displayName.split(',')[0].trim() : null);
}

function notify(key, name) {
    const subs = subscribers.get(key);
    if (subs) subs.forEach(fn => { try { fn(name); } catch (e) { /* unmounted */ } });
}

async function worker() {
    if (running) return;
    running = true;
    while (queue.length > 0) {
        const { key, lat, lng } = queue.shift();
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=14&accept-language=en`);
            const data = await res.json();
            const name = shortName(data?.address, data?.display_name);
            if (name) {
                mem.set(key, name);
                persistCache();
                notify(key, name);
            }
        } catch (e) { /* offline / rate-limited — row keeps showing Locating… */ }
        pending.delete(key);
        if (queue.length > 0) await new Promise(r => setTimeout(r, 1100));
    }
    running = false;
}

/** Cache-only peek; queues a lookup when unknown. Returns name or null. */
export function peekPlaceName(lat, lng) {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln) || (la === 0 && ln === 0)) return null;
    loadCache();
    const key = keyOf(la, ln);
    const hit = mem.get(key);
    if (hit) return hit;
    if (!pending.has(key)) {
        pending.add(key);
        queue.push({ key, lat: la, lng: ln });
        worker();
    }
    return null;
}

/** React hook: nearby place name for coordinates (null while resolving). */
export function usePlaceName(lat, lng) {
    const la = parseFloat(lat), ln = parseFloat(lng);
    const valid = Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0);
    const key = valid ? keyOf(la, ln) : null;
    const [name, setName] = useState(() => (valid ? peekPlaceName(la, ln) : null));

    useEffect(() => {
        if (!key) { setName(null); return undefined; }
        const current = peekPlaceName(la, ln);
        setName(current);
        if (current) return undefined;
        if (!subscribers.has(key)) subscribers.set(key, new Set());
        const subs = subscribers.get(key);
        subs.add(setName);
        return () => { subs.delete(setName); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return name;
}
